import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import ArrowLeft from '@/images/arrows/ArrowLeft.svg';
import {
    ScrollLeftButton,
    ScrollLeftButtonGradient,
    ScrollRightButton,
    ScrollRightButtonGradient,
    SectionTitle,
} from '@/pages/home/components/Common';
import styled from '@emotion/styled';
import { ReactNode, useEffect, useRef, useState } from 'react';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    outline: none;
`;

const ItemsContainer = styled.div`
    display: flex;
    position: relative;
    outline: none;
`;

const ScrollContainer = styled.div`
    display: flex;
    gap: 16px;
    outline: none;

    /* Hide scrollbar for Chrome, Safari and Opera */
    ::-webkit-scrollbar {
        display: none;
    }

    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    overflow-x: scroll;
`;

interface Props {
    id: string;
    title?: string;
    children: ReactNode;
    className?: string;
    scrollButtonPadding?: number;
}

export const ContentBar = ({ id, title, children, className, scrollButtonPadding = 0 }: Props) => {
    const ref = useRef<HTMLDivElement | null>(null);

    const [showRightScroll, setShowRightScroll] = useState<boolean>(false);
    const [showLeftScroll, setShowLeftScroll] = useState<boolean>(false);

    const calculateScroll = () => {
        const scrollWidth = ref?.current?.scrollWidth || 0;
        const offsetWidth = ref?.current?.offsetWidth || 0;
        const scrollLeft = ref?.current?.scrollLeft || 0;

        setShowRightScroll(scrollWidth > offsetWidth + scrollLeft);
        setShowLeftScroll(!!ref.current?.scrollLeft);
    };

    useEffect(() => {
        calculateScroll();
        window.addEventListener('resize', calculateScroll);
        ref.current?.addEventListener('scroll', calculateScroll);

        return () => {
            window.removeEventListener('resize', calculateScroll);
            ref.current?.removeEventListener('scroll', calculateScroll);
        };
    }, [children]);

    const handleScrollRight = () => {
        ref?.current?.scrollBy({ left: 1000, behavior: 'smooth' });
    };

    const handleScrollLeft = () => {
        ref?.current?.scrollBy({ left: -1000, behavior: 'smooth' });
    };

    return (
        <Container className={className}>
            {!!title && <SectionTitle id={id}>{title}</SectionTitle>}

            <ItemsContainer>
                {showLeftScroll && (
                    <>
                        <ScrollLeftButtonGradient scrollButtonPadding={scrollButtonPadding} />
                        <ScrollLeftButton onClick={handleScrollLeft} scrollButtonPadding={scrollButtonPadding}>
                            <MaskedIcon src={ArrowLeft} size="24px" />
                        </ScrollLeftButton>
                    </>
                )}

                <ScrollContainer ref={ref}>{children}</ScrollContainer>

                {showRightScroll && (
                    <>
                        <ScrollRightButtonGradient scrollButtonPadding={scrollButtonPadding} />
                        <ScrollRightButton onClick={handleScrollRight} scrollButtonPadding={scrollButtonPadding}>
                            <MaskedIcon src={ArrowLeft} size="24px" rotated />
                        </ScrollRightButton>
                    </>
                )}
            </ItemsContainer>
        </Container>
    );
};
